/**
 * frontend/src/sdk/hooks/use-benchmarks.ts
 *
 * Typed TanStack Query hooks for the model benchmark leaderboard.
 *
 * Backend: GET /api/benchmarks/leaderboard
 *
 * IMPORTANT: these scores are SIMULATED by default. The benchmark suite runs
 * a deterministic mock simulator (hand-authored per-model bias profiles)
 * unless a real inference function is wired in — every entry carries
 * `inference_mode` / `simulated`, and the UI must label simulated rankings
 * plainly. Presenting simulated rankings as real model performance is a
 * trust/diligence risk.
 */

import { useQuery } from "@tanstack/react-query";

import { apiGet } from "@/lib/api-client";

export interface BenchmarkLeaderboardEntry {
  rank: number;
  run_id: string;
  timestamp: string;
  model_key: string;
  mean_brier_score: number;
  mean_absolute_error: number;
  calibration_error: number;
  direction_accuracy: number;
  mean_response_time_ms: number;
  total_cost_usd: number;
  composite_score: number;
  n_scenarios: number;
  inference_mode?: string;
  simulated?: boolean;
}

export interface BenchmarkLeaderboardResponse {
  success: boolean;
  leaderboard: BenchmarkLeaderboardEntry[];
  n_models: number;
  simulated?: boolean;
  note?: string | null;
}

export function useBenchmarkLeaderboard() {
  return useQuery({
    queryKey: ["benchmarks", "leaderboard"],
    queryFn: () =>
      apiGet<BenchmarkLeaderboardResponse>("/api/benchmarks/leaderboard"),
    staleTime: 300_000,
    refetchInterval: 600_000,
  });
}
